import { get } from "lodash-es";
import { type Doc } from "sharedb/lib/client";

import {
  BlockType,
  CellType,
  CodeCellType,
  FieldType,
  FileCellType,
  MultipleChoiceCellType,
  RichTextCellType,
  TableDimensionsType,
  WorksheetType,
} from "../types/worksheet";

import { BLOCKS_PATH, STATIC_CONTENT_FIELD_TYPES } from "./constants";

const zero_width_non_breaking_space = "\\ufeff";
const non_breaking_space = "\\00a0";

export const getDeltaText = (value: RichTextCellType) => {
  let fieldContent = "";

  if (value) {
    const ops = value.ops;
    if (ops) {
      ops.forEach((op) => {
        if (op.insert && typeof op.insert === "string") {
          const insert: string = op.insert.trim();
          fieldContent += insert
            .replace(non_breaking_space, " ")
            .replace(zero_width_non_breaking_space, " ");
        } else if (op.insert && typeof op.insert === "object" && op.insert.image) {
          fieldContent += op.insert.image as string; // count image url as content
        }
      });
    }
  }

  return fieldContent;
};

export const measurableBlockFields = {
  FreeResponse: "RichTextField",
  MultipleChoice: "ChoiceField",
  Code: "CodeField",
  Table: "TableField",
} as const;

export const measurableBlockTypes = Object.keys(measurableBlockFields);

const flattenFields = (doc: Doc<WorksheetType>): { id: string; type: FieldType["t"] }[] => {
  const blocks = get(doc.data, BLOCKS_PATH);
  return blocks.flatMap((block) =>
    block.f.map((field) => ({
      id: field.id,
      type: field.t,
    })),
  );
};

/**
 * For any block, this will return the field within that block that we measure "progress"
 * for, if any. Used in e.g. "2/4" students made progress on this block, and also used for
 * the engagement dashboard effort scores.
 */
export const getMeasuredFieldFromBlock = (block: BlockType) => {
  return block.f.filter((f) => !STATIC_CONTENT_FIELD_TYPES.includes(f.t)).at(-1);
};

const canDetectProgressForField = (type: FieldType["t"]) => {
  return ["RichTextField", "TableField", "ChoiceField", "CodeField", "FileField"].includes(type);
};

export const fieldProgressForUserCellData = (type: FieldType["t"], userFieldData: CellType) => {
  if (!canDetectProgressForField(type)) {
    return { touched: false, charCount: 0 };
  }

  if (type === "RichTextField") {
    const richTextValue = getDeltaText(userFieldData as RichTextCellType);
    const length = richTextValue?.length ?? 0;
    return { touched: length > 0, charCount: length };
  }

  if (type === "TableField") {
    let richTextValue = "";
    const cells = (userFieldData as TableDimensionsType)?.cm;
    if (!cells) {
      return { touched: false, charCount: 0 };
    }

    Object.values(cells).forEach((cell) => {
      // ignore cells student's cannot update
      richTextValue += cell.ro || !cell.v ? "" : getDeltaText(cell.v);
    });
    const length = richTextValue.length;

    return { touched: length > 0, charCount: length };
  }

  if (type === "ChoiceField") {
    const length = (userFieldData as MultipleChoiceCellType)?.length ?? 0;
    return {
      touched: length > 0,
      charCount: 0,
    };
  }

  if (type === "CodeField") {
    const length = (userFieldData as CodeCellType)?.c?.trim().length ?? 0;
    return {
      touched: length > 0,
      charCount: length,
    };
  }

  if (type === "FileField") {
    const length = (userFieldData as FileCellType)?.length ?? 0;
    return {
      touched: length > 0,
      charCount: 0,
    };
  }

  throw new Error(`Unknown field type: ${type}`);
};

export const getUserFieldProgress = (
  doc: Doc<WorksheetType>,
  fieldId: string,
  accessId: string,
): { touched: boolean; charCount: number } => {
  const flatFields = flattenFields(doc);
  const userData = doc.data.d[accessId];
  const userFieldData = userData?.[fieldId];
  const field = flatFields.find((f) => f.id === fieldId);

  if (!userFieldData || !field) {
    return { touched: false, charCount: 0 };
  }

  const fieldType = field.type;

  if (!fieldType) {
    return { touched: false, charCount: 0 };
  }

  return fieldProgressForUserCellData(fieldType, userFieldData);
};

/**
 * Check if a particular student has touched any blocks in a document.
 * Returns true if the student has made progress on any measurable field in any block.
 */
export const hasStudentTouchedAnyBlocks = (doc: Doc<WorksheetType>, accessId: string): boolean => {
  const blocks = get(doc.data, BLOCKS_PATH);

  if (!blocks || !Array.isArray(blocks)) {
    return true;
  }

  // Check each block for measurable fields
  for (const block of blocks) {
    const measuredField = getMeasuredFieldFromBlock(block);

    if (measuredField) {
      const progress = getUserFieldProgress(doc, measuredField.id, accessId);
      if (progress.touched) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Count number of responses that haven't touched any blocks.
 */
export const getUntouchedResponses = <T extends { id: string }>(
  doc: Doc<WorksheetType>,
  responses: T[],
): T[] => {
  if (!doc) {
    return [];
  }

  const untouched: T[] = [];
  for (const response of responses) {
    if (response.id && !hasStudentTouchedAnyBlocks(doc, response.id)) {
      untouched.push(response);
    }
  }

  return untouched;
};
